/// <amd-module name="TFS/Admin/Views/OrganizationPolicies/Policy" />
import React = require("react");
import AdminResources = require("TFS/Admin/Views/OrganizationPolicies/Resources");

import * as StringUtil from "VSS/Core/Util/String";

import { ComponentConstants, HostTypeConstants, IPolicy, PolicyValue } from "TFS/Admin/Views/OrganizationPolicies/Contracts/DataModels";
import { Components, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IObservableArray, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { Icon, IconSize } from "VSSUI/Icon";

import { Button } from "VSSUI/Button";
import { Dropdown } from "VSSUI/Dropdown";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { IListBoxItem } from "VSSUI/ListBox";
import { Link } from "VSSUI/Link";
import { Observer } from "VSSUI/Observer";
import { Toggle } from "VSSUI/Toggle";
import { Tooltip } from "VSSUI/TooltipEx";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";

export interface Props {
    policy: IPolicy;
    onChangeHandler(key: string, value: boolean, enforce: boolean): void;
    hasPolicyPermissions: boolean;
    isOrganizationActivated: boolean;
    keyId: string;
    hasUrl: boolean;
    onEditUrlClick(policyDescription: string, policyLink: string): void;
}

export interface PolicySelection {
    value?: IListBoxItem;
    selection: DropdownSelection;
}

const EmptyInfoURL = "about:blank";

export class Policy extends VssComponent<Props, any> {
    private toggleOption: ObservableValue<boolean>;

    constructor(props: Props, context?: any) {
        super(props, context);
        this.toggleOption = new ObservableValue(true);
    }

    public componentDidMount() {
        this.setDataProvider();
    }

    public componentDidUpdate() {
        this.setDataProvider();
    }

    public render(): JSX.Element {
        let policy = this.props.policy.policy;
        return (
            <div className="policy-row-container">
                {
                    <Toggle
                        className="policy-toggle"
                        offText={AdminResources.Off}
                        offAriaLabel={AdminResources.Off}
                        onText={AdminResources.On}
                        onAriaLabel={AdminResources.On}
                        checked={this.toggleOption}
                        onChange={this.onToggleChanged}
                        disabled={
                            !this.props.hasPolicyPermissions ||
                            (this.props.isOrganizationActivated && policy.parentPolicy && policy.parentPolicy.enforce)
                        }
                    />
                }
                <div className="policy-name-container">
                    <span>{this.props.policy.description ? this.props.policy.description : policy.name}</span>
                    {localeIgnoreCaseComparer(this.props.policy.learnMoreLink, EmptyInfoURL) !== 0 && (
                        <Tooltip
                            text={StringUtil.format(
                                AdminResources.AriaLabelLearnMoreAbout,
                                this.props.policy.description ? this.props.policy.description : policy.name
                            )}
                        >
                            <Link
                                ariaLabel={StringUtil.format(
                                    AdminResources.AriaLabelLearnMoreAbout,
                                    this.props.policy.description ? this.props.policy.description : policy.name
                                )}
                                id={"learn-more-" + policy.name}
                                href={this.props.policy.learnMoreLink}
                                target=" _blank"
                                className="policy-description-container"
                            >
                                <Icon className="icon-margin" iconName="Link" size={IconSize.small} />
                            </Link>
                        </Tooltip>
                    )}
                    { this.props.hasUrl ? (
                    <Button
                        primary={false}
                        className="policy-editurl-button"                        
                        onClick= {() => {this.props.onEditUrlClick(this.props.policy.description, this.props.policy.learnMoreLink)}}
                        disabled={this.props.policy.policy.effectiveValue}
                    >
                    {AdminResources.EditUrlButtonLabel}
                    </Button>
                ) : null}
                </div>
            </div>
        );
    }

    private setDataProvider() {
        const selection = mapFromPolicyAndContextToEnumKey(this.props.policy, this.props.isOrganizationActivated);
        this.toggleOption.value = selection === (PolicyValue.On as number);
    }

    private onToggleChanged = (event: React.SyntheticEvent<HTMLElement>, value: boolean) => {
        this.toggleOption.value = value;
        this.props.onChangeHandler(this.props.keyId, value, false); // Enforce in collection always false
    };
}

function mapFromPolicyAndContextToEnumKey(policy: IPolicy, isOrganizationActivated: boolean): number {
    if (isOrganizationActivated && policy.policy.parentPolicy && policy.policy.parentPolicy.enforce) {
        // Not sure if enforcement is still relevant
        if (
            policy.policy.parentPolicy.effectiveValue
                .toString()
                .trim()
                .toLowerCase() == ComponentConstants.TRUE_AS_STRING
        ) {
            return PolicyValue.On;
        }
        return PolicyValue.Off;
    }

    if (
        policy.policy.effectiveValue
            .toString()
            .trim()
            .toLowerCase() == ComponentConstants.TRUE_AS_STRING
    ) {
        return PolicyValue.On;
    }
    return PolicyValue.Off;
}
